import { useMemo } from 'react'
import { cn } from '../../shared/utils/cn'
import { DeviceInfo } from '../../common/enums'

interface ExportDeviceSelectionProps {
  devices: Record<string, DeviceInfo>
  selectedDevices: Set<string>
  currentDeviceId: string
  onSelectionChange: (sigfoxId: string, selected: boolean) => void
  onSelectAll: (selected: boolean) => void
}

// Check if device is online (had reading within last 2 hours)
function isDeviceOnline(device: DeviceInfo): boolean {
  if (!device.lastReadingTimestamp) return false

  let lastReadingTime: number
  if (typeof device.lastReadingTimestamp === 'number') {
    // Could be seconds or milliseconds - check magnitude
    lastReadingTime = device.lastReadingTimestamp > 1e12
      ? device.lastReadingTimestamp
      : device.lastReadingTimestamp * 1000
  } else if (typeof device.lastReadingTimestamp === 'string') {
    lastReadingTime = new Date(device.lastReadingTimestamp).getTime()
  } else if (device.lastReadingTimestamp instanceof Date) {
    lastReadingTime = device.lastReadingTimestamp.getTime()
  } else {
    return false
  }

  const twoHoursAgo = Date.now() - (2 * 60 * 60 * 1000)
  return lastReadingTime > twoHoursAgo
}

export function ExportDeviceSelection({
  devices,
  selectedDevices,
  currentDeviceId,
  onSelectionChange,
  onSelectAll
}: ExportDeviceSelectionProps) {
  // Sort devices alphabetically by name, with current device first
  const sortedDevices = useMemo(() => {
    return Object.values(devices).sort((a, b) => {
      if (a.sigfoxId === currentDeviceId) return -1
      if (b.sigfoxId === currentDeviceId) return 1
      return (a.deviceName || a.sigfoxId).localeCompare(b.deviceName || b.sigfoxId)
    })
  }, [devices, currentDeviceId])

  const totalDevices = sortedDevices.length
  const selectedCount = selectedDevices.size

  // Determine select all state
  const selectAllState = useMemo((): 'all' | 'none' | 'some' => {
    if (totalDevices === 0) return 'none'
    if (selectedCount === totalDevices) return 'all'
    if (selectedCount === 0) return 'none'
    return 'some'
  }, [selectedCount, totalDevices])

  const handleSelectAllChange = () => {
    onSelectAll(selectAllState !== 'all')
  }

  return (
    <div className="space-y-3 h-full flex flex-col">
      <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">
        Devices
      </label>

      {/* Select All */}
      <div className="flex items-center gap-2 pb-2 border-b border-gray-100">
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={selectAllState === 'all'}
            ref={(el) => {
              if (el) el.indeterminate = selectAllState === 'some'
            }}
            onChange={handleSelectAllChange}
            className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
          />
          <span className="text-sm font-medium text-gray-700">
            Select All
          </span>
        </label>
        <span className="text-xs text-gray-500">
          ({selectedCount}/{totalDevices})
        </span>
      </div>

      {/* Device List - flex-1 to fill available height */}
      <div className="flex-1 overflow-y-auto border border-gray-200 rounded-md min-h-0">
        {sortedDevices.map((device) => {
          const isSelected = selectedDevices.has(device.sigfoxId)
          const isCurrent = device.sigfoxId === currentDeviceId
          const isOnline = isDeviceOnline(device)

          return (
            <div
              key={device.sigfoxId}
              className={cn(
                'flex flex-col gap-0.5 px-3 py-2',
                'border-b border-gray-100 last:border-b-0',
                isSelected ? 'bg-blue-50/50' : 'bg-white'
              )}
            >
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={isSelected}
                  onChange={(e) => onSelectionChange(device.sigfoxId, e.target.checked)}
                  className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 shrink-0"
                />
                {/* Online/Offline indicator dot */}
                <span className={cn(
                  'w-2 h-2 rounded-full shrink-0',
                  isOnline ? 'bg-green-500' : 'bg-gray-400'
                )} />
                <span className={cn(
                  'text-sm truncate',
                  isSelected ? 'text-gray-900 font-medium' : 'text-gray-700'
                )}>
                  {device.deviceName || device.sigfoxId}
                </span>
                {isCurrent && (
                  <span className="inline-flex items-center px-1.5 py-0.5 text-xs font-medium text-blue-700 bg-blue-100 rounded shrink-0">
                    Current
                  </span>
                )}
              </label>
              {device.siteName && (
                <span className="text-xs text-gray-400 ml-6 truncate">
                  {device.siteName}
                </span>
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}
